import { type UseQueryResult, useQueries } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import type { DayWageStats, DepartmentWageDataResponse } from "../utils/models";
import { toIsoDate, weekStartsBetween } from "../utils/scheduleWeeks";
import { prefetchScheduleWagesWeek, scheduleWagesWeekQuery } from "./scheduleCache";
import { usePrefetchNeighbourWeeks } from "./usePrefetchNeighbourWeeks";
import { useScheduleCacheEnabled, useScheduleStartDay } from "./useScheduleWeekRules";

export type ScheduleWages = {
	/** Each day's wage totals by ISO date, for the days from `from` through `to` that have arrived. */
	dayWageStats: Record<string, DayWageStats>;
	dayWageStatsActual: Record<string, DayWageStats>;
	isLoading: boolean;
	isError: boolean;
};

const inRange = (stats: Record<string, DayWageStats>, from: string, to: string): Record<string, DayWageStats> =>
	Object.fromEntries(Object.entries(stats).filter(([date]) => date >= from && date <= to));

/**
 * Each day's wage totals from `from` through `to`, one cached week per request, on the same weeks as
 * `useScheduleDays`, with the same prefetch of the weeks around. Nothing is asked for when the department hides
 * wages; the totals stay empty.
 */
export const useScheduleWages = (from: DateTime, to: DateTime): ScheduleWages => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const enabled = useScheduleCacheEnabled() && !chosenDepartment.hide_wage;
	const startDay = useScheduleStartDay();
	const { trackError } = useAnalytics();
	const fromDate = toIsoDate(from);
	const toDate = toIsoDate(to);

	const weekStarts = useMemo(() => weekStartsBetween(DateTime.fromISO(fromDate), DateTime.fromISO(toDate), startDay), [fromDate, toDate, startDay]);

	const combine = useCallback(
		(results: UseQueryResult<DepartmentWageDataResponse>[]): ScheduleWages => {
			const weeks = results.flatMap((result) => (result.data ? [result.data] : []));
			return {
				dayWageStats: inRange(Object.assign({}, ...weeks.map((week) => week.dayWageStats)), fromDate, toDate),
				dayWageStatsActual: inRange(Object.assign({}, ...weeks.map((week) => week.dayWageStatsActual)), fromDate, toDate),
				isLoading: results.some((result) => result.isLoading),
				isError: results.some((result) => result.isError),
			};
		},
		[fromDate, toDate],
	);

	const wages = useQueries({ queries: weekStarts.map((weekStart) => ({ ...scheduleWagesWeekQuery(departmentId, weekStart), enabled })), combine });

	useEffect(() => {
		if (wages.isError) trackError("schedule_wages_fetch_error", { department_id: departmentId, start_date: fromDate, end_date: toDate });
	}, [wages.isError, departmentId, fromDate, toDate, trackError]);

	usePrefetchNeighbourWeeks(departmentId, weekStarts, enabled && !wages.isLoading && !wages.isError, prefetchScheduleWagesWeek);

	return wages;
};
